import React, { FunctionComponent, useEffect, useState } from 'react';
import ModalBase from '../components/ModalBase';
import InputFieldTitle from '../components/InputFieldTitle';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import { Category, ProjectProperties } from '../types';
import useURLParams from '../hooks/useURLParams';
import { createProject } from '../utils/backendServices';
import { useNavigateWithinOrg } from '../utils/navigation';
import { useProjectLibraryContext } from '../contexts/ProjectLibraryContext';

type InputField = {
    label: string;
    fieldName: keyof ProjectProperties;
    inputType: 'text' | 'dropdown';
    options?: string[];
    defaultValue?: string;
};

const allFields: InputField[] = [
    {
        fieldName: 'name',
        label: 'Project Name',
        inputType: 'text',
    },
    {
        fieldName: 'category',
        label: 'Category',
        inputType: 'dropdown',
        options: Object.values(Category) as Category[],
    },
];

type FormData = {
    [key in (typeof allFields)[number]['fieldName']]: string;
};

const CreateProjectModal: FunctionComponent = () => {
    const defaultFormData = allFields.reduce(
        (acc, field) => ({
            ...acc,
            [field.fieldName]: field.defaultValue ?? '',
        }),
        {},
    ) as FormData;
    const [formData, setFormData] = useState<FormData>(defaultFormData);

    const { setParam } = useURLParams();
    const navigate = useNavigateWithinOrg();

    const {
        setters: { setIsLoading },
        refresh,
    } = useProjectLibraryContext();

    const handleInputChange = (fieldName: keyof FormData, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
    };
    const canSubmit = allFields.every((field) => formData[field.fieldName]);

    const handleCreation = async () => {
        if (canSubmit) {
            const newProjectData = formData;
            setParam('modal', null);
            setIsLoading(true);
            const projectId = await createProject(
                newProjectData as ProjectProperties,
            );
            setIsLoading(false);
            navigate(`projects/${projectId}`);
            refresh();
        }
    };

    return (
        <ModalBase onClose={() => setParam('modal', null)} width="500px">
            <div className="upload-components">
                <h2 className="upload-title">Create New Project</h2>
                <p style={{ textAlign: 'center' }}>
                    A project houses all labels and specifications related to
                    one product.
                </p>
                {allFields.map((field) => (
                    <div key={field.fieldName}>
                        <InputFieldTitle title={field.label} required={true} />
                        {field.inputType === 'text' ? (
                            <input
                                type="text"
                                className="user-input-text"
                                value={formData[field.fieldName] || ''}
                                onChange={(e) =>
                                    handleInputChange(
                                        field.fieldName,
                                        e.target.value,
                                    )
                                }
                            />
                        ) : field.inputType === 'dropdown' && field.options ? (
                            <select
                                className="user-input-dropdown"
                                value={formData[field.fieldName] || ''}
                                onChange={(e) =>
                                    handleInputChange(
                                        field.fieldName,
                                        e.target.value,
                                    )
                                }
                            >
                                <option value="" disabled>
                                    -- Select an option --
                                </option>
                                {field.options.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                        ) : null}
                    </div>
                ))}
                <SignedIn>
                    <button
                        className="submit-button-upload"
                        onClick={handleCreation}
                        disabled={!canSubmit}
                    >
                        Create
                    </button>
                </SignedIn>
                <SignedOut>
                    <SignInButton>
                        <button
                            className="submit-button-upload"
                            disabled={!canSubmit}
                        >
                            Create
                        </button>
                    </SignInButton>
                </SignedOut>
            </div>
        </ModalBase>
    );
};

export default CreateProjectModal;
